import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { PLANS } from "../../constants/testIds";
import { useCelebration } from "../../contexts/CelebrationContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FormField, isFieldValid } from "../common/FormField";
import { SubmitButton } from "../common/SubmitButton";

const PRICING_TYPES = [
  { value: "subscription", label: "Subscription (recurring plan)" },
  { value: "session_pack", label: "Session pack" },
  { value: "pay_per_class", label: "Pay per class" },
];

// Create/edit a plan. Editing NEVER mutates the existing plan doc — `PATCH /plans/{id}` inserts
// a new version with the same name, so a member already on the old version keeps their pinned
// price/rules untouched (see routers/plans.py's own comment). `name` is therefore locked on edit.
export function PlanFormDialog({ open, onOpenChange, plan }) {
  const isEdit = !!plan;
  const queryClient = useQueryClient();
  const { celebrate } = useCelebration();
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState("");

  function emptyForm() {
    return { name: "", type: "subscription", amount: "", joiningFee: "0", duration: "", sessionsCap: "", freezeMax: "0", freezeUnit: "times", roaming: false, maxDiscount: "100" };
  }

  useEffect(() => {
    if (!open) return;
    setError("");
    if (plan) {
      setForm({
        name: plan.name, type: plan.pricing.type, amount: String(plan.pricing.amount / 100),
        joiningFee: String((plan.pricing.joining_fee || 0) / 100), duration: plan.duration ? String(plan.duration) : "",
        sessionsCap: plan.caps?.sessions != null ? String(plan.caps.sessions) : "",
        freezeMax: String(plan.freeze_rules?.max ?? 0), freezeUnit: plan.freeze_rules?.unit || "times",
        roaming: !!plan.roaming_allowed, maxDiscount: String(plan.max_discount_percent ?? 100),
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, plan]);

  const buildPayload = () => ({
    ...(isEdit ? {} : { name: form.name }),
    pricing: { type: form.type, amount: Math.round(Number(form.amount) * 100), joining_fee: Math.round(Number(form.joiningFee || 0) * 100) },
    duration: form.duration ? Number(form.duration) : null,
    caps: { sessions: form.sessionsCap ? Number(form.sessionsCap) : null, classes: null, class_types: [] },
    freeze_rules: { max: Number(form.freezeMax || 0), unit: form.freezeUnit },
    roaming_allowed: form.roaming,
    max_discount_percent: Number(form.maxDiscount || 100),
  });

  const mutation = useMutation({
    mutationFn: () => (isEdit ? api.patch(`/plans/${plan.id}`, buildPayload()) : api.post("/plans", buildPayload())),
    onSuccess: () => {
      if (!isEdit) celebrate("plan_created", `${form.name} is ready to sell.`);
      queryClient.invalidateQueries({ queryKey: ["plans-list"] });
      onOpenChange(false);
    },
    onError: (e) => setError(formatApiError(e).message),
  });

  const isValid = (isEdit || isFieldValid("text", true, form.name)) && isFieldValid("number", true, form.amount) && Number(form.amount) >= 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal sm:rounded-[14px] max-h-[85vh] overflow-y-auto" data-testid={PLANS.formDialog}>
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${plan.name}` : "Add plan"}</DialogTitle>
          <DialogDescription>{isEdit ? "Saves as a new version — members already on this plan keep their old price." : "Members can be enrolled on this the moment it's saved."}</DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm shake-on-error" style={{ color: "var(--scarlet-neg)" }} data-testid={PLANS.formError}>{error}</p>}

        <div className="space-y-4">
          {!isEdit && <FormField label="Plan name" required testId={PLANS.nameInput} value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="e.g. Gold Annual" />}
          <div className="space-y-1.5">
            <Label>Pricing type</Label>
            <Select onValueChange={(v) => setForm({ ...form, type: v })} value={form.type}>
              <SelectTrigger data-testid={PLANS.typeSelect}><SelectValue /></SelectTrigger>
              <SelectContent>{PRICING_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Amount (₹)" required type="number" testId={PLANS.amountInput} value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} />
            <FormField label="Joining fee (₹)" type="number" testId={PLANS.joiningFeeInput} value={form.joiningFee} onChange={(v) => setForm({ ...form, joiningFee: v })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Duration (days)" type="number" testId={PLANS.durationInput} value={form.duration} onChange={(v) => setForm({ ...form, duration: v })} helperText="Leave blank for session/class packs." />
            <FormField label="Session cap" type="number" testId={PLANS.sessionsCapInput} value={form.sessionsCap} onChange={(v) => setForm({ ...form, sessionsCap: v })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Freeze allowance" type="number" testId={PLANS.freezeMaxInput} value={form.freezeMax} onChange={(v) => setForm({ ...form, freezeMax: v })} />
            <div className="space-y-1.5">
              <Label>Freeze unit</Label>
              <Select onValueChange={(v) => setForm({ ...form, freezeUnit: v })} value={form.freezeUnit}>
                <SelectTrigger data-testid={PLANS.freezeUnitSelect}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="times">Times</SelectItem><SelectItem value="days">Days</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <FormField label="Max discount staff can apply (%)" type="number" testId={PLANS.maxDiscountInput} value={form.maxDiscount} onChange={(v) => setForm({ ...form, maxDiscount: v })} />
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox data-testid={PLANS.roamingCheckbox} checked={form.roaming} onCheckedChange={(v) => setForm({ ...form, roaming: !!v })} />
            Allow roaming across branches
          </label>
        </div>

        <DialogFooter>
          <SubmitButton
            testId={PLANS.formSubmit} disabled={!isValid} pending={mutation.isPending}
            onClick={() => { setError(""); mutation.mutate(); }} label={isEdit ? "Save new version" : "Add plan"}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
